'use client';

import { ItemTrackingStatus } from '@/domain/roadmap/types';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { PlanningViewSideBar } from './PlanningView/PlanningViewSideBar';

type QaSummary = {
  approved: number;
  pending: number;
};

type PlanningBulkEditContextValue = {
  isOpen: boolean;
  toggleOpen: () => void;
  close: () => void;
  selectedCount: number;
  selectedItemIds: Set<number>;
  statusSummary: Partial<Record<ItemTrackingStatus, number>>;
  qaSummary: QaSummary;
  viewOnly: boolean;
  isPlanningTab: boolean;
  hasSelectedItem: boolean;
  applyChanges: (
    perGroupMap: Partial<Record<ItemTrackingStatus, ItemTrackingStatus>>,
    qaPerGroupMap: Partial<Record<string, boolean>>,
  ) => void;
};

const PlanningBulkEditContext = createContext<PlanningBulkEditContextValue | null>(null);

interface PlanningBulkEditProviderProps {
  isPlanningTab: boolean;
  hasSelectedItem: boolean;
  viewOnly: boolean;
  selectedItemIds: Set<number>;
  statusSummary: Partial<Record<ItemTrackingStatus, number>>;
  qaSummary: QaSummary;
  children: ReactNode;
}

export function PlanningBulkEditProvider({
  isPlanningTab,
  hasSelectedItem,
  viewOnly,
  selectedItemIds,
  statusSummary,
  qaSummary,
  children,
}: Readonly<PlanningBulkEditProviderProps>) {
  const [isOpen, setIsOpen] = useState(false);

  const applyChanges = useCallback(
    (
      perGroupMap: Partial<Record<ItemTrackingStatus, ItemTrackingStatus>>,
      qaPerGroupMap: Partial<Record<string, boolean>>,
    ) => {
      const { editableRoadMap } = useRoadmapEditStore.getState();
      if (
        selectedItemIds.size === 0 ||
        (Object.keys(perGroupMap).length === 0 && Object.keys(qaPerGroupMap).length === 0)
      ) {
        return;
      }

      const updatedStatuses = editableRoadMap.type?.roadmapStatus?.map((statusGroup) => ({
        ...statusGroup,
        roadmapStatusItems: statusGroup.roadmapStatusItems.map((item) => {
          if (!selectedItemIds.has(item.idRoadmapStatusItem)) return item;
          const targetStatus = perGroupMap[item.status];
          const targetQa = qaPerGroupMap[String(item.qualityAssurance)];
          const hasChange = targetStatus !== undefined || targetQa !== undefined;
          if (!hasChange) return item;
          const statusPatch = targetStatus === undefined ? {} : { status: targetStatus };
          const qaPatch = targetQa === undefined ? {} : { qualityAssurance: targetQa };
          return { ...item, ...statusPatch, ...qaPatch, edited: true };
        }),
      }));

      if (updatedStatuses) {
        useRoadmapEditStore.setState({
          editableRoadMap: {
            ...editableRoadMap,
            type: editableRoadMap.type ? { ...editableRoadMap.type, roadmapStatus: updatedStatuses } : null,
          },
          isStoreDirty: true,
        });
      }
    },
    [selectedItemIds],
  );

  const value = useMemo(
    () => ({
      isOpen,
      toggleOpen: () => setIsOpen((prev) => !prev),
      close: () => setIsOpen(false),
      selectedCount: selectedItemIds.size,
      selectedItemIds,
      statusSummary,
      qaSummary,
      viewOnly,
      isPlanningTab,
      applyChanges,
      hasSelectedItem,
    }),
    [applyChanges, hasSelectedItem, isOpen, isPlanningTab, qaSummary, selectedItemIds, statusSummary, viewOnly],
  );

  return <PlanningBulkEditContext.Provider value={value}>{children}</PlanningBulkEditContext.Provider>;
}

function usePlanningBulkEditContext() {
  const context = useContext(PlanningBulkEditContext);
  if (!context) {
    throw new Error('PlanningBulkEdit components must be used inside PlanningBulkEditProvider');
  }
  return context;
}

export function PlanningBulkEditTrigger() {
  const { isOpen, toggleOpen, selectedItemIds, viewOnly, isPlanningTab } = usePlanningBulkEditContext();

  if (!isPlanningTab) return null;

  return viewOnly ? null : (
    <Button
      size="small"
      variant={isOpen ? 'contained' : 'outlined'}
      startIcon={<EditOutlined fontSize="small" />}
      disabled={selectedItemIds.size === 0}
      onClick={toggleOpen}
    >
      Bulk Edit
    </Button>
  );
}

export function PlanningBulkEditSidebar() {
  const { isOpen, close, selectedCount, statusSummary, qaSummary, applyChanges, isPlanningTab, hasSelectedItem } =
    usePlanningBulkEditContext();

  if (!isPlanningTab || !isOpen || !hasSelectedItem) return null;

  return (
    <Box
      sx={{
        borderLeft: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        flexShrink: 0,
        alignSelf: 'stretch',
      }}
    >
      <PlanningViewSideBar
        selectedCount={selectedCount}
        statusSummary={statusSummary}
        qaSummary={qaSummary}
        onApply={(perGroupMap, qaPerGroupMap) => {
          applyChanges(perGroupMap, qaPerGroupMap);
          close();
        }}
        onClose={close}
      />
    </Box>
  );
}
